import { AxiosError, AxiosResponse } from "axios";
import { API, APIResponse } from "@/components/_Shared/API/API";
import AxiosInstance from "../components/Axios";
import {
	CalendarResponse,
	CalendarView,
} from "../interfaces/calendar_type";
import {
	ProjectFormCreationProps,
	ProjectProps,
	ProjectStatus,
} from "../interfaces/project_types";
import {
	CREATE_PROJECT,
	DELETE_PROJECT,
	GET_ACTIVE_PROJECTS,
	GET_PROJECT_BY_DATE,
	GET_PROJECT_BY_ID,
	GET_PROJECTS,
	UPDATE_CONSULTING_FEE,
	UPDATE_PROJECT,
	UPDATE_PROJECT_DESCRIPTION,
	UPDATE_PROJECT_DETAIL,
	UPDATE_PROJECT_STATUS,
	PUT_UNNEEDED_TEMPLATES,
} from "./Endpoints/projects";

/**
 * Fetches a list of projects
 *
 * uses the project_list() view in the backend
 *
 * @ filter optional: filters the list of projects based on field
 * @ returns (supposed to be) a list of ProjectProps
 */
export async function getProjectList(
	filter?: string,
): Promise<APIResponse<ProjectProps[]>> {
	const response = await API.get<ProjectProps[]>(GET_PROJECTS, {
		params: filter ? { filter } : {},
	});
	return response;
}

export async function getActiveProjects(): Promise<
	APIResponse<ProjectProps[]>
> {
	const response = await API.get<ProjectProps[]>(GET_ACTIVE_PROJECTS);
	return response;
}

/**
 * Fetches a single project based on id
 *
 * @param id the project id of the project
 * @returns An object of type UpdateProjectProps
 */
export async function getProject(
	id: string,
): Promise<APIResponse<ProjectProps>> {
	const response = await API.get<ProjectProps>(
		GET_PROJECT_BY_ID.replace("{id}", id),
	);
	return response;
}

/**
 * Creates a Trinity Project
 *
 * The project_data should be in the format of ProjectPOSTProps
 *
 * @param project_data
 */
export async function createProject(
	project_data: ProjectFormCreationProps,
): Promise<AxiosResponse> {
	const response = await AxiosInstance.post(CREATE_PROJECT, project_data);
	return response;
}

export async function updateProject(
	project_data: ProjectFormCreationProps,
): Promise<AxiosResponse> {
	const response = await AxiosInstance.put(
		UPDATE_PROJECT.replace("{id}", project_data.project_id ?? ""),
		project_data,
	);
	return response;
}

export async function deleteProject(id: string): Promise<number> {
	try {
		const response = await AxiosInstance.delete(
			DELETE_PROJECT.replace("{id}", id),
		);
		return response.status;
	} catch (error: unknown) {
		if (error instanceof AxiosError) {
			if (error.response?.status === 400) {
				// 400 Bad Request
				return 400;
			} else if (error.response?.status === 403) {
				// 403 Forbidden
				return 403;
			}
		}
		console.error("Server Error: ", error);
		return 500;
	}
}

export async function getCalendarProjects(
	view: CalendarView,
	date: string,
	userOnly = false,
): Promise<CalendarResponse> {
	try {
		const response = await AxiosInstance.get<CalendarResponse>(
			GET_PROJECT_BY_DATE,
			{
				params: { view, date, ...(userOnly ? { user_only: "true" } : {}) },
			},
		);

		return response.data;
	} catch (error) {
		console.error("Server Error: ", error);
		throw error;
	}
}

export async function updateConsultingFee(
	project_id: string,
	consulting_fee: number,
): Promise<number> {
	try {
		const response = await AxiosInstance.put(
			UPDATE_CONSULTING_FEE.replace("{id}", project_id),
			{ consulting_fee: consulting_fee },
		);
		return response.status;
	} catch (error) {
		console.error("Server Error: ", error);
		return 500;
	}
}

export async function updateProjectDescription(
	project_id: string,
	description: string,
): Promise<number> {
	try {
		const response = await AxiosInstance.put(
			UPDATE_PROJECT_DESCRIPTION.replace("{id}", project_id),
			{ description: description },
		);
		return response.status;
	} catch (error) {
		console.error("Server Error: ", error);
		return 500;
	}
}

export async function updateProjectStatus(
	project_id: string,
	status: ProjectStatus,
): Promise<number> {
	try {
		const response = await AxiosInstance.put(
			UPDATE_PROJECT_STATUS.replace("{id}", project_id),
			{ status: status },
		);
		return response.status;
	} catch (error) {
		console.error("Server Error: ", error);
		return 500;
	}
}

export async function updateProjectDetail(
	project_id: string,
	property: string,
	value: string,
): Promise<number> {
	try {
		const response = await AxiosInstance.put(
			UPDATE_PROJECT_DETAIL.replace("{id}", project_id),
			{ property: property, value: value },
		);
		return response.status;
	} catch (error) {
		console.error("Server Error: ", error);
		return 500;
	}
}

export async function putUnneededTemplates(
	project_id: string,
	template_ids: number[],
): Promise<APIResponse<{ unneeded_template_ids: number[] }>> {
	const response = await API.put<{ unneeded_template_ids: number[] }>(
		PUT_UNNEEDED_TEMPLATES.replace("{id}", project_id),
		{ template_ids },
	);
	return response;
}

